/**
 * Contact Table - List view of contacts
 */
import React from "react";
import { 
  Phone, Mail, MapPin, ChevronDown, MoreVertical, 
  Trophy, Archive, Snowflake, AlertTriangle, Flame,
  Clock, Trash2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Format phone for display
const formatPhone = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Format currency
const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Time ago helper
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

// Stage badge component
const StageBadge = ({ stage, stages }) => {
  const config = stages?.find(s => s.key === stage) || { 
    label: stage, 
    color: "bg-slate-500", 
    textColor: "text-slate-400" 
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} text-white`}>
      {config.label}
    </span>
  );
};

// Contact row component
const ContactRow = ({ 
  contact, 
  onClick, 
  onStageChange, 
  onMarkLost, 
  onMoveToIcebox,
  onWakeFromIcebox,
  onDelete,
  canDelete,
  stages,
  isArchive,
  archiveType
}) => {
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  const location = [contact.address_city, contact.address_state].filter(Boolean).join(", ");
  
  return (
    <tr 
      className="border-b border-slate-800 hover:bg-slate-800/50 cursor-pointer transition-colors group"
      onClick={() => onClick(contact)}
    >
      {/* Name & Contact */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Stagnant indicator */}
          {contact.is_stagnant && (
            <div className="w-1 h-10 bg-amber-500 rounded-full" title="Stagnant - needs attention" />
          )}
          <div>
            <p className="font-medium text-slate-100 group-hover:text-emerald-400 transition-colors">
              {fullName || "Unknown"}
            </p>
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
              {contact.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {formatPhone(contact.phone)}
                </span>
              )}
              {contact.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {contact.email}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>

      {/* Source */}
      <td className="px-4 py-3">
        <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
          {contact.source?.replace(/_/g, " ") || "Manual"}
        </Badge>
      </td>

      {/* Service */}
      <td className="px-4 py-3">
        <span className="text-sm text-slate-300">
          {contact.service_type?.replace(/_/g, " ") || "-"}
        </span>
      </td>

      {/* Value */}
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-slate-100">
          {formatCurrency(contact.estimated_value || contact.final_value)}
        </span>
      </td>

      {/* Stage/Status */}
      <td className="px-4 py-3">
        {isArchive ? (
          <Badge className={`
            ${archiveType === 'won' ? 'bg-emerald-500/20 text-emerald-400' : ''}
            ${archiveType === 'lost' ? 'bg-red-500/20 text-red-400' : ''}
            ${archiveType === 'icebox' ? 'bg-cyan-500/20 text-cyan-400' : ''}
          `}>
            {archiveType === 'won' && <Trophy className="h-3 w-3 mr-1" />}
            {archiveType === 'lost' && <Archive className="h-3 w-3 mr-1" />}
            {archiveType === 'icebox' && <Snowflake className="h-3 w-3 mr-1" />}
            {archiveType?.charAt(0).toUpperCase() + archiveType?.slice(1)}
          </Badge>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="flex items-center gap-1 hover:opacity-80 transition-opacity">
                <StageBadge stage={contact.stage} stages={stages} />
                <ChevronDown className="h-3 w-3 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700">
              {stages?.map(stage => (
                <DropdownMenuItem
                  key={stage.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStageChange(contact.contact_id, stage.key);
                  }}
                  className={`cursor-pointer ${contact.stage === stage.key ? 'bg-slate-800' : ''}`}
                >
                  <span className={`w-2 h-2 rounded-full ${stage.color} mr-2`} />
                  {stage.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>

      {/* Last Activity */}
      <td className="px-4 py-3">
        <span className={`text-xs ${contact.is_stagnant ? 'text-amber-400' : 'text-slate-500'}`}>
          {timeAgo(contact.last_contact_at || contact.created_at)}
        </span>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-white">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            {!isArchive && (
              <>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onClick(contact); }}
                  className="cursor-pointer"
                >
                  View Details
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onMoveToIcebox(contact); }}
                  className="cursor-pointer text-cyan-400"
                >
                  <Snowflake className="h-4 w-4 mr-2" />
                  Move to Icebox
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onMarkLost(contact); }}
                  className="cursor-pointer text-red-400"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Mark as Lost
                </DropdownMenuItem>
              </>
            )}
            {isArchive && archiveType === 'icebox' && (
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onWakeFromIcebox(contact.contact_id); }}
                className="cursor-pointer text-emerald-400"
              >
                <Flame className="h-4 w-4 mr-2" />
                Wake Up Now
              </DropdownMenuItem>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onDelete(contact); }}
                  className="cursor-pointer text-red-500 focus:text-red-400"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

const ContactTable = ({ 
  contacts, 
  loading, 
  onContactClick, 
  onStageChange, 
  onMarkLost, 
  onMoveToIcebox,
  onWakeFromIcebox,
  onDelete,
  canDelete,
  stages,
  isArchive,
  archiveType
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-900/50 rounded-lg border border-slate-800">
        <p className="text-slate-500">No contacts found</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-900/50 rounded-lg border border-slate-800 overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-900">
          <tr className="border-b border-slate-800">
            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Contact</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Source</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Service</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Value</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
              {isArchive ? "Status" : "Stage"}
            </th>
            <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Last Activity</th>
            <th className="w-12"></th>
          </tr>
        </thead>
        <tbody>
          {contacts.map(contact => (
            <ContactRow
              key={contact.contact_id}
              contact={contact}
              onClick={onContactClick}
              onStageChange={onStageChange}
              onMarkLost={onMarkLost}
              onMoveToIcebox={onMoveToIcebox}
              onWakeFromIcebox={onWakeFromIcebox}
              onDelete={onDelete}
              canDelete={canDelete}
              stages={stages}
              isArchive={isArchive}
              archiveType={archiveType}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ContactTable;
